import { useState } from 'react';

const SERVER_URL = process.env.REACT_APP_API_HOST || 'http://localhost:8080';

interface QbSyncResult {
  ok: boolean;
  message: string;
  qbEstimateId?: string;
  status?: string;
  emailStatus?: string;
}

interface QbPollOpenUpdate {
  quoteId: number;
  previousStatus: string;
  newStatus: string;
  qbStatus: string;
}

interface QbPollOpenResult {
  ok: boolean;
  message: string;
  updates?: QbPollOpenUpdate[];
}

export interface QbCustomerPreviewRow {
  qbCustomerId: string;
  name: string;
  email: string | null;
  phone: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  matchedCustomerId: number | null;
  alreadyLinked: boolean;
}

interface QbCustomerPreviewResult {
  ok: boolean;
  message: string;
  customers?: QbCustomerPreviewRow[];
  truncated?: boolean;
}

interface QbCustomerImportResult {
  ok: boolean;
  message: string;
  created?: number;
  linked?: number;
  addressesCreated?: number;
  errors?: Array<{ name: string; error: string }>;
}

// Shared client for the /qb/estimate, /qb/estimate-status, and
// /qb/customers/* routes, used by the quote list, quote detail page, and the
// Customers page's "Sync Quickbooks Customers" dialog.
export function useQuickBooksSync() {
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const [isPollingOpen, setIsPollingOpen] = useState(false);
  const [isSyncingCustomers, setIsSyncingCustomers] = useState(false);

  const setPending = (quoteId: number, pending: boolean) => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (pending) next.add(quoteId);
      else next.delete(quoteId);
      return next;
    });
  };

  const isPending = (quoteId: number) => pendingIds.has(quoteId);

  const sendToQuickBooks = async (quoteId: number): Promise<QbSyncResult> => {
    setPending(quoteId, true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${SERVER_URL}/qb/estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ quoteId }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'QuickBooks sync failed');
      return {
        ok: true,
        message: `Created QuickBooks estimate ${body.docNumber} ($${Number(body.total).toFixed(2)})`,
        qbEstimateId: String(body.estimateId),
      };
    } catch (err: any) {
      return { ok: false, message: err.message || 'QuickBooks sync failed' };
    } finally {
      setPending(quoteId, false);
    }
  };

  const checkQuickBooksStatus = async (quoteId: number): Promise<QbSyncResult> => {
    setPending(quoteId, true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${SERVER_URL}/qb/estimate-status?quoteId=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'QuickBooks status check failed');
      return {
        ok: true,
        message: body.changed
          ? `QuickBooks status is "${body.qbStatus}" — quote updated to "${body.status}"`
          : `QuickBooks status is "${body.qbStatus || 'unchanged'}" — no update needed`,
        status: body.status,
        emailStatus: body.emailStatus,
      };
    } catch (err: any) {
      return { ok: false, message: err.message || 'QuickBooks status check failed' };
    } finally {
      setPending(quoteId, false);
    }
  };

  const pollOpenQuotes = async (): Promise<QbPollOpenResult> => {
    setIsPollingOpen(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${SERVER_URL}/qb/estimate-status/poll-open`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'QuickBooks batch status check failed');

      const { checked, updated, updates, emailUpdated, chunkErrors, skipped } = body;
      let message: string;
      if (checked === 0) {
        message = 'No open quotes to check';
      } else {
        message = `Checked ${checked}, ${updated} updated`;
        if (emailUpdated > 0) {
          message += `, ${emailUpdated} email status update${emailUpdated === 1 ? '' : 's'}`;
        }
        if (chunkErrors?.length > 0) {
          message += ` (${chunkErrors.length} batch(es) failed — try again)`;
        }
        if (skipped > 0) {
          message += ` — ${skipped} older open quote${skipped === 1 ? '' : 's'} not checked this round`;
        }
      }

      return { ok: !(chunkErrors?.length > 0), message, updates };
    } catch (err: any) {
      return { ok: false, message: err.message || 'QuickBooks batch status check failed' };
    } finally {
      setIsPollingOpen(false);
    }
  };

  const previewCustomerSync = async (): Promise<QbCustomerPreviewResult> => {
    setIsSyncingCustomers(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${SERVER_URL}/qb/customers/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'QuickBooks customer sync failed');
      return {
        ok: true,
        message: `Found ${body.customers?.length || 0} QuickBooks customer${body.customers?.length === 1 ? '' : 's'}`,
        customers: body.customers,
        truncated: body.truncated,
      };
    } catch (err: any) {
      return { ok: false, message: err.message || 'QuickBooks customer sync failed' };
    } finally {
      setIsSyncingCustomers(false);
    }
  };

  const importCustomers = async (
    customers: QbCustomerPreviewRow[],
  ): Promise<QbCustomerImportResult> => {
    setIsSyncingCustomers(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${SERVER_URL}/qb/customers/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ customers }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'QuickBooks customer import failed');

      const { created, linked, addressesCreated, errors } = body;
      let message = `Imported ${created || 0} new, linked ${linked || 0} existing customer${(linked || 0) === 1 ? '' : 's'}`;
      if (addressesCreated > 0) {
        message += `, ${addressesCreated} address${addressesCreated === 1 ? '' : 'es'} added`;
      }
      if (errors?.length > 0) {
        message += ` (${errors.length} skipped)`;
      }

      return { ok: true, message, created, linked, addressesCreated, errors };
    } catch (err: any) {
      return { ok: false, message: err.message || 'QuickBooks customer import failed' };
    } finally {
      setIsSyncingCustomers(false);
    }
  };

  return {
    sendToQuickBooks,
    checkQuickBooksStatus,
    pollOpenQuotes,
    previewCustomerSync,
    importCustomers,
    isPending,
    isPollingOpen,
    isSyncingCustomers,
  };
}
